import type { AudioProvider, Track, PlaybackState, PlaybackListener } from "../types/audio";
import { isNativePlatform } from "../lib/platform";

// Web prod routes through the Netlify proxy, which internally maps ?id= to
// iTunes /lookup and ?term= to iTunes /search. Dev + native hit Apple directly,
// so they need distinct endpoints (search?id=... returns nothing).
const DIRECT_ITUNES = import.meta.env.DEV || isNativePlatform();
const ITUNES_SEARCH_URL = DIRECT_ITUNES ? "https://itunes.apple.com/search" : "/api/itunes";
const ITUNES_LOOKUP_URL = DIRECT_ITUNES ? "https://itunes.apple.com/lookup" : "/api/itunes";

/**
 * Primary audio provider using the iTunes Search API.
 *
 * - No auth/API key required
 * - Preview URLs are direct M4A files with CORS enabled
 * - Full seek control via Web Audio API
 * - Rate limit: ~20 requests/minute (search only, playback is unlimited)
 */
export class ITunesProvider implements AudioProvider {
  private audioContext: AudioContext | null = null;
  private audioBuffer: AudioBuffer | null = null;
  private sourceNode: AudioBufferSourceNode | null = null;
  private state: PlaybackState = { status: "idle", currentTime: 0, duration: 0 };
  private listeners: Set<PlaybackListener> = new Set();
  private progressInterval: ReturnType<typeof setInterval> | null = null;
  private clipStartTime = 0;
  private clipDuration = 0;
  private playbackStartedAt = 0;

  async lookupById(itunesId: string): Promise<Track | null> {
    try {
      const url = `${ITUNES_LOOKUP_URL}?id=${encodeURIComponent(itunesId)}`;
      const resp = await fetch(url);
      if (!resp.ok) return null;
      // iTunes serves valid JSON bodies with `Content-Type: text/javascript`
      // for legacy JSONP compatibility. Don't gate on the header — let
      // resp.json() throw instead, which the catch below handles.
      const data = await resp.json();
      const result = data.results?.[0];
      if (!result?.previewUrl) return null;
      return {
        id: String(result.trackId),
        title: result.trackName,
        artist: result.artistName,
        previewUrl: result.previewUrl,
        artworkUrl: result.artworkUrl100?.replace("100x100", "600x600"),
        genre: result.primaryGenreName,
        year: result.releaseDate ? new Date(result.releaseDate).getFullYear() : undefined,
        provider: "itunes",
      };
    } catch {
      return null;
    }
  }

  async search(artist: string, title: string): Promise<Track | null> {
    // Try artist + title first, then title only (helps with Hebrew songs)
    for (const term of [`${artist} ${title}`, title]) {
      try {
        const url = `${ITUNES_SEARCH_URL}?term=${encodeURIComponent(term)}&media=music&entity=song&limit=5`;
        const resp = await fetch(url);
        if (!resp.ok) continue;
        const data = await resp.json();
        if (!data.results?.length) continue;

        const result = this.findBestMatch(data.results, artist, title);
        if (result) return result;
      } catch {
        continue;
      }
    }
    return null;
  }

  private findBestMatch(results: any[], artist: string, title: string): Track | null {

    const titleLower = title.toLowerCase();
    const artistLower = artist.toLowerCase();

    const match =
      results.find(
        (r: any) =>
          r.previewUrl &&
          r.trackName.toLowerCase().includes(titleLower) &&
          r.artistName.toLowerCase().includes(artistLower)
      ) ??
      results.find((r: any) => r.previewUrl);

    if (!match?.previewUrl) return null;

    return {
      id: String(match.trackId),
      title: match.trackName,
      artist: match.artistName,
      previewUrl: match.previewUrl,
      artworkUrl: match.artworkUrl100?.replace("100x100", "600x600"),
      genre: match.primaryGenreName,
      year: match.releaseDate ? new Date(match.releaseDate).getFullYear() : undefined,
      provider: "itunes",
    };
  }

  /** Ensure AudioContext exists and is resumed (call from user gesture) */
  async ensureContext(): Promise<void> {
    if (!this.audioContext) {
      this.audioContext = new AudioContext();
    }
    if (this.audioContext.state === "suspended") {
      await this.audioContext.resume();
    }
  }

  async load(track: Track): Promise<void> {
    this.stop();
    this.setState({ status: "loading", currentTime: 0, duration: 0 });

    await this.ensureContext();

    // Fetch the preview audio file (CORS enabled on Apple's CDN)
    const resp = await fetch(track.previewUrl);
    const arrayBuffer = await resp.arrayBuffer();

    // Decode into an AudioBuffer for precise timing control
    this.audioBuffer = await this.audioContext!.decodeAudioData(arrayBuffer);

    this.setState({
      status: "idle",
      currentTime: 0,
      duration: this.audioBuffer.duration,
    });
  }

  async playClip(startSec: number, durationSec: number): Promise<void> {
    if (!this.audioContext || !this.audioBuffer) {
      throw new Error("No track loaded. Call load() first.");
    }

    // Stop any current playback
    this.stopSource();

    // Resume AudioContext if suspended (browser autoplay policy)
    if (this.audioContext.state === "suspended") {
      await this.audioContext.resume();
    }

    // Clamp start position within the preview duration
    const maxStart = Math.max(0, this.audioBuffer.duration - durationSec);
    const clampedStart = Math.min(startSec, maxStart);

    this.clipStartTime = clampedStart;
    this.clipDuration = durationSec;

    // Create a new source node (they are single-use)
    this.sourceNode = this.audioContext.createBufferSource();
    this.sourceNode.buffer = this.audioBuffer;
    this.sourceNode.connect(this.audioContext.destination);

    // Play from startSec for durationSec
    this.sourceNode.start(0, clampedStart, durationSec);
    this.playbackStartedAt = this.audioContext.currentTime;

    this.setState({ status: "playing", currentTime: clampedStart, duration: this.audioBuffer.duration });

    // Track progress
    this.progressInterval = setInterval(() => {
      if (!this.audioContext || this.state.status !== "playing") return;
      const elapsed = this.audioContext.currentTime - this.playbackStartedAt;
      this.setState({
        ...this.state,
        status: "playing",
        currentTime: this.clipStartTime + elapsed,
      });
    }, 50);

    // Auto-stop when clip ends
    this.sourceNode.onended = () => {
      this.clearProgress();
      this.setState({
        ...this.state,
        status: "ended",
        currentTime: this.clipStartTime + this.clipDuration,
      });
    };
  }

  pause(): void {
    if (this.audioContext?.state === "running") {
      this.audioContext.suspend();
      this.clearProgress();
      this.setState({ ...this.state, status: "paused" });
    }
  }

  stop(): void {
    this.stopSource();
    this.clearProgress();
    this.audioBuffer = null;
    this.setState({ status: "idle", currentTime: 0, duration: 0 });
  }

  onStateChange(listener: PlaybackListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  getState(): PlaybackState {
    return { ...this.state };
  }

  getAudioBuffer(): AudioBuffer | null {
    return this.audioBuffer;
  }

  // --- Private ---

  private stopSource(): void {
    try {
      this.sourceNode?.stop();
    } catch {
      // Already stopped
    }
    this.sourceNode?.disconnect();
    this.sourceNode = null;

    if (this.audioContext?.state === "suspended") {
      this.audioContext.resume();
    }
  }

  private clearProgress(): void {
    if (this.progressInterval) {
      clearInterval(this.progressInterval);
      this.progressInterval = null;
    }
  }

  private setState(newState: PlaybackState): void {
    this.state = newState;
    for (const listener of this.listeners) {
      listener({ ...this.state });
    }
  }
}
